import {
  isTeammateAnEditor,
  isTeammateAReviewer,
  isTeammateATeamMember,
} from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import {
  IconActivity,
  IconBook2,
  IconChartBar,
  IconClockBolt,
  IconHome,
  IconLayoutDashboard,
  IconPhone,
  IconUsersGroup,
} from "@tabler/icons-react";
import type { FC } from "react";
import { useLocation, useParams } from "react-router";
import { NavigationSearchContainer } from "../../../features/search/navigationSearchContainer.js";
import { selectTeammate } from "../../../features/teammate/teammateSlice.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { useAppSelector } from "../../../hooks/hook.js";
import { performanceSections } from "../performanceSections.js";
import { matchesNavPath, NavigationMenu } from "./navigationMenu.js";
import type { NavigationMenuItemProps } from "./navigationMenuItem.js";

export const PerformanceNavigationMenu: FC = () => {
  const { playbookId } = useParams();
  const { pathname } = useLocation();
  const currentTeammate = useAppSelector(selectTeammate);
  const areTopicsAndSignalsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.TopicsAndSignals,
  );
  const isReportsEnabled = useIsFeatureFlagEnabled(FeatureFlagKey.Reports);

  if (playbookId === undefined) {
    return (
      <NavigationMenu
        items={[]}
        search={<NavigationSearchContainer context="team_performance" />}
      />
    );
  }

  const isCurrentTeammateAMemberOnly =
    currentTeammate && isTeammateATeamMember(currentTeammate);

  // The Automations section is for editors and reviewers (reviewers
  // get a read-only view); keep it out of the nav for everyone else.
  const canCurrentTeammateViewAutomations =
    currentTeammate &&
    (isTeammateAnEditor(currentTeammate) ||
      isTeammateAReviewer(currentTeammate));

  const buildItem = (
    section: {
      label: string;
      path: { build: (params: { playbookId: string }) => string };
    },
    icon: NavigationMenuItemProps["icon"],
  ): NavigationMenuItemProps => {
    const href = section.path.build({ playbookId });
    return {
      label: section.label,
      href,
      icon,
      isActive: matchesNavPath(pathname, href),
    };
  };

  // Team members only have access to their own teammate page (see
  // `teamMemberRouter` in app/index.tsx), so none of the team-level nav items
  // are reachable for them.
  const items: readonly NavigationMenuItemProps[] =
    !isCurrentTeammateAMemberOnly
      ? [
          buildItem(
            performanceSections.overview,
            <IconHome aria-hidden className="size-4 shrink-0" />,
          ),
          buildItem(
            performanceSections.team,
            <IconUsersGroup aria-hidden className="size-4 shrink-0" />,
          ),
          buildItem(
            performanceSections.calls,
            <IconPhone aria-hidden className="size-4 shrink-0" />,
          ),
          buildItem(
            performanceSections.playbook,
            <IconBook2 aria-hidden className="size-4 shrink-0" />,
          ),
          ...(canCurrentTeammateViewAutomations
            ? [
                buildItem(
                  performanceSections.automations,
                  <IconClockBolt aria-hidden className="size-4 shrink-0" />,
                ),
              ]
            : []),
          ...(areTopicsAndSignalsEnabled
            ? [
                buildItem(
                  performanceSections.topics,
                  <IconLayoutDashboard
                    aria-hidden
                    className="size-4 shrink-0"
                  />,
                ),
                buildItem(
                  performanceSections.signals,
                  <IconActivity aria-hidden className="size-4 shrink-0" />,
                ),
              ]
            : []),
          ...(isReportsEnabled
            ? [
                buildItem(
                  performanceSections.reports,
                  <IconChartBar aria-hidden className="size-4 shrink-0" />,
                ),
              ]
            : []),
        ]
      : [];

  return (
    <NavigationMenu
      items={items}
      search={<NavigationSearchContainer context="team_performance" />}
    />
  );
};
